import Plugin from '@ckeditor/ckeditor5-core/src/plugin';

// ZWJ + VS16 for complex emoji sequences
const ZWJ = '\u200D';
const VS16 = '\uFE0F';

// 🔥 ONLY real Unicode emoji (surrogate pairs)
// NO © ® ™ NO BMP symbols
const maybeEmojiRE =
	/\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDDFF]/;

function looksLikeEmoji( str ) {
	return !!str && (
		maybeEmojiRE.test( str ) ||
		str.includes( ZWJ ) ||
		str.includes( VS16 )
	);
}

export default class EmojiAfterInsertWrap extends Plugin {
	static get pluginName() {
		return 'EmojiAfterInsertWrap';
	}

	init() {
		const editor = this.editor;

		// 1️⃣ Allow emoji attribute on text
		editor.model.schema.extend( '$text', { allowAttributes: [ 'emoji' ] } );

		// 2️⃣ Upcast: <span class="emoji emoji-ckedit-custom">😊</span> → text + emoji attribute
		editor.conversion.for( 'upcast' ).elementToAttribute( {
			view: { name: 'span', classes: [ 'emoji', 'emoji-ckedit-custom' ] },
			model: { key: 'emoji', value: true }
		} );

		// 3️⃣ Downcast: emoji attribute → <span class="emoji emoji-ckedit-custom">
		editor.conversion.for( 'downcast' ).attributeToElement( {
			model: 'emoji',
			view: ( value, { writer } ) => {
				if ( !value ) {
					return null;
				}

				return writer.createAttributeElement(
					'span',
					{ class: 'emoji emoji-ckedit-custom' },
					{ priority: 5 }
				);
			}
		} );

		// 4️⃣ Normalize existing content ONCE
		this._normalizeAllEmojiOnce();

		// 5️⃣ Normalize only NEW insertions (typing / paste)
		this.listenTo( editor.model.document, 'change:data', () => {
			this._normalizeRecentInsertions();

			// Never keep emoji attribute on caret
			editor.model.change( writer => {
				writer.removeSelectionAttribute( 'emoji' );
			} );
		} );

		// 6️⃣ Hard safety: selection must never carry emoji attribute
		editor.model.document.registerPostFixer( writer => {
			const sel = editor.model.document.selection;

			if ( sel.hasAttribute( 'emoji' ) ) {
				writer.removeSelectionAttribute( 'emoji' );
				return true;
			}

			return false;
		} );
	}

	// 🔍 Scan full document once (initial load)
	_normalizeAllEmojiOnce() {
		const editor = this.editor;

		editor.model.change( writer => {
			const root = editor.model.document.getRoot();

			const visit = element => {
				for ( const child of element.getChildren() ) {
					if ( child.is( '$text' ) ) {
						if ( looksLikeEmoji( child.data ) ) {
							const start = writer.createPositionBefore( child );
							const end = writer.createPositionAfter( child );

							writer.setAttribute( 'emoji', true, writer.createRange( start, end ) );
						}
					}
					else if ( child.is( 'element' ) ) {
						visit( child );
					}
				}
			};

			visit( root );
		} );
	}

	// 🔥 Normalize ONLY newly inserted nodes (safe path)
	_normalizeRecentInsertions() {
		const editor = this.editor;
		const differ = editor.model.document.differ;

		editor.model.change( writer => {
			for ( const change of differ.getChanges() ) {
				if ( change.type !== 'insert' ) {
					continue;
				}

				// Case 1: direct text insertion
				if ( change.name === '$text' ) {
					const pos = change.position;
					const node = pos.nodeAfter;

					if ( node && node.is( '$text' ) && looksLikeEmoji( node.data ) ) {
						const len = node.data.length;
						const start = pos;
						const end = pos.getShiftedBy( len );

						writer.setAttribute( 'emoji', true, writer.createRange( start, end ) );
						writer.setSelection( end ); // caret after emoji
					}

					continue;
				}

				// Case 2: pasted element subtree
				const el = change.position.nodeAfter;

				if ( el && el.is( 'element' ) ) {
					const stack = [ el ];

					while ( stack.length ) {
						const cur = stack.pop();

						for ( const ch of cur.getChildren() ) {
							if ( ch.is( '$text' ) && looksLikeEmoji( ch.data ) ) {
								const before = writer.createPositionBefore( ch );
								const after = writer.createPositionAfter( ch );

								writer.setAttribute( 'emoji', true, writer.createRange( before, after ) );
							}
							else if ( ch.is( 'element' ) ) {
								stack.push( ch );
							}
						}
					}
				}
			}
		} );
	}
}
